import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { pairsStore } from "@/shared/stores/PairsStore";
import { tickerStore } from "@/shared/stores/TickerStore";
import { BetType, type BetMessage } from "@cronika/proto/common/trading_pb";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useState } from "react";
import { getServerNowMs } from "@/shared/utils/serverTime";
import { clampToMonotonicRemainingMs } from "@/shared/utils/monotonicCountdown";
import { DirectionBadge } from "../../../components/DirectionBadge";

dayjs.extend(duration);

interface ActiveBetCardProps {
  bet: BetMessage & {
    timeLeftMs: number;
    progress: number;
    _targetMs?: number;
    _uiKey?: string;
  };
  animate: boolean;
  index: number;
}

export const ActiveBetCard: FC<ActiveBetCardProps> = observer(({ bet, animate, index }) => {
  const betSymbol = bet.symbol;

  const pair = pairsStore.pairs.find((p) => p.symbol === bet.symbol);
  const icon = useSymbolIcon(pair?.type, pair?.baseAsset, pair?.quoteAsset);

  const entryPrice = Number(bet?.entryPrice) || 0;
  const amount = Number(bet?.amount) || 0;
  const payoutPercent = Number(bet?.payoutPercent) || 0;
  const minRaisePoints = Number(bet?.minRaisePoints ?? 0);

  const currentPrice = betSymbol ? (tickerStore.prices.get(betSymbol) ?? null) : null;

  const betState = useMemo(() => {
    const isCall = bet?.betType === BetType.CALL;
    const threshold = entryPrice * minRaisePoints * 0.001;

    if (!currentPrice || !entryPrice) {
      return null;
    }

    let state: "winning" | "losing" | "returned";
    if (isCall) {
      if (currentPrice >= entryPrice + threshold) state = "winning";
      else if (currentPrice < entryPrice) state = "losing";
      else state = "returned";
    } else {
      if (currentPrice <= entryPrice - threshold) state = "winning";
      else if (currentPrice > entryPrice) state = "losing";
      else state = "returned";
    }

    return state;
  }, [currentPrice, entryPrice, minRaisePoints, bet?.betType]);

  const profitText = useMemo(() => {
    if (betState === "winning") {
      const profit = (amount * payoutPercent) / 100;
      return `+$${profit.toFixed(2)}`;
    }
    if (betState === "losing") return `-$${amount.toFixed(2)}`;
    return "$0.00";
  }, [betState, amount, payoutPercent]);

  const [nowMs, setNowMs] = useState(getServerNowMs);
  useEffect(() => {
    const id = setInterval(() => setNowMs(getServerNowMs()), 250);
    return () => clearInterval(id);
  }, []);

  const timeLeft = useMemo(() => {
    const rawMs = bet?._targetMs ? Math.max(0, bet._targetMs - nowMs) : Math.max(0, bet?.timeLeftMs ?? 0);
    const key = bet?._uiKey ?? bet?.id;
    const ms = key ? clampToMonotonicRemainingMs(key, rawMs) : rawMs;
    const secs = Math.ceil(ms / 1000);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [bet?.timeLeftMs, bet?._targetMs, bet?._uiKey, bet?.id, nowMs]);

  useEffect(() => {
    if (!betSymbol) return;
    tickerStore.subscribe(betSymbol);
    return () => tickerStore.unsubscribe(betSymbol);
  }, [betSymbol]);

  if (!bet) return null;

  return (
    <div
      className={`px-1.5 py-1.5 bg-default/60 rounded-xl ${
        animate ? "animate-in fade-in-0 slide-in-from-top-2 duration-700 ease-out" : ""
      }`}
      style={
        animate
          ? {
              animationDelay: `${Math.min(index, 6) * 40}ms`,
              animationFillMode: "backwards",
            }
          : undefined
      }
    >
      <div
        className="flex items-start gap-1.5 cursor-pointer"
        onClick={() => pair && pairsStore.replaceActivePair(pair)}
      >
        <img src={icon} alt={bet.symbol} className="h-5 w-5 shrink-0 object-cover" />
        <div className="flex flex-1 flex-col gap-1">
          {/* Row 1: symbol • payout ... time left — same line, so the timer
              actually lines up with the text it belongs to. */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-gray-500">
              <p className="text-[10px]">{bet.symbol}</p>
              <p className="text-[10px]">•</p>
              <p className="text-success text-[10px]">{payoutPercent}%</p>
            </div>
            <p className="text-[10px] text-gray-500 whitespace-nowrap">{timeLeft}</p>
          </div>
          {/* Row 2: amount + direction ... P&L — same pairing, one row down. */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <p className="text-[12px]">${parseFloat(bet.amount).toFixed(2)}</p>
              <DirectionBadge betType={bet.betType} />
            </div>
            <p
              className={`ml-auto text-[12px] font-semibold whitespace-nowrap ${
                betState === "winning" ? "text-success" : betState === "losing" ? "text-danger" : "text-default-500"
              }`}
            >
              {profitText}
            </p>
          </div>
        </div>
      </div>
      {bet.isCopyTradingTrade && (
        <div className="mt-2 -mx-3 -mb-3 rounded-b-xl flex items-center justify-center h-[26px] bg-default-500">
          <p className="text-[11px] font-bold text-black">COPY TRADE</p>
        </div>
      )}
    </div>
  );
});
